// "In Review" (formerly status 2) was removed — nothing in the app ever set it, so
// the workflow is Draft → Published (→ Amended / Distributed). See issue #12.
export type ReportStatus = 'Draft' | 'Published' | 'Amended' | 'Distributed';
export type SectionKey = 'exec_summary' | 'safety' | 'customer' | 'sales' | 'operations' | 'financial';
export type ChangeType = 'Edit' | 'StatusChange' | 'Publish' | 'Clone' | 'VisualRefresh';

// ─── Chart data types ─────────────────────────────────────────────────────────

export type ChartType = 'bar' | 'line' | 'kpi' | 'matrix' | 'card';
export type ChartFormat = 'currency' | 'percent' | 'number' | 'decimal';

// Flat row format used by Recharts BarChart and LineChart
export type ChartRow = Record<string, string | number>;

export interface KpiData {
  label: string;
  value: number;
  format?: ChartFormat;
  invertComparison?: boolean;
  comparison?: {
    value: number;
    label?: string;
  };
  sparkline?: {
    data: number[];
    target?: number[]; // per-month budget/target series (#21) — overlaid as a budget line
    labels?: string[]; // short month labels, e.g. "Apr-25"
    type?: 'line' | 'bar';
  };
}

export interface MatrixData {
  columns: string[];
  rows: { label: string; values: (number | string | null)[] }[];
  // colFormats[colIndex][rowIndex] = % achievement (decimal, e.g. 0.95 = 95%).
  // Drives conditional formatting: <95% red, 95–100% yellow, ≥100% green.
  colFormats?: Record<string, (number | null)[]>;
}

export interface CardItem {
  label: string;
  value: string | number;
  subLabel?: string;
}

export interface VisualDataResponse {
  chartType: ChartType;
  title: string;
  data: ChartRow[] | KpiData | MatrixData | CardItem[];
  meta?: {
    index?: string;      // x-axis column key for bar/line
    keys?: string[];     // series keys (if omitted, all non-index keys are used)
    unit?: ChartFormat;
    stacked?: boolean;
  };
}

export interface Report {
  id: string;
  name: string;
  region: string;
  branches: string[];
  divisions: string[];
  month: number;
  year: number;
  monthYear: string;
  status: ReportStatus;
  preparedBy: string;
  publishedOn?: string;
  publishedBy?: string;
  sourceReportId?: string;
  templateVersion?: string;
  createdOn: string;
  modifiedOn: string;
}

export interface ReportSection {
  id: string;
  reportId: string;
  sectionKey: SectionKey;
  content: string;
  content2: string;
  content3: string;
}

export interface ReportAuditEntry {
  id: string;
  reportId: string;
  sectionId?: string;
  fieldName: string;
  valueBefore: string;
  valueAfter: string;
  changedBy: string;
  changedByName: string;
  changedOn: string;
  changeType: ChangeType;
}

/** Which document a Word template renders: 1 = Cover page, 2 = Report body. */
export type TemplateKind = 1 | 2;

export const TEMPLATE_KIND_COVER: TemplateKind = 1;
export const TEMPLATE_KIND_REPORT: TemplateKind = 2;

export interface ReportTemplate {
  id: string;
  name: string;
  blobPath: string;
  isActive: boolean;
  uploadedOn: string;
  uploadedBy: string;
  notes?: string;
  kind: TemplateKind;
}

export type CombinedStatus = 'Draft' | 'Published' | 'Distributed';

export interface CombinedReportSummary {
  id: string;
  coverTitle: string;
  coverDate: string;
  coverPreparedBy: string;
  status: CombinedStatus;
  generatedOn: string;
  reportCount: number;
}

export interface DistributeResult {
  distributed: number;
  skipped: { reportId: string; reason: string }[];
}

export interface NewReportContext {
  region: string;
  branches: string[];
  divisions: string[];
  month: number;
  year: number;
  populateFromPrevious: boolean;
}

export type UserRole = 'Standard' | 'Builder' | 'Admin';
/** What User Access can add: entry and Standard come from the portal (#704). */
export type AddedRole = 'Builder' | 'Admin';

export interface MeResponse {
  id: string;
  name: string;
  email: string;
  /** Does the portal admit this user (status + team ticks)? No roles when it doesn't. */
  entitled: boolean;
  roles: UserRole[];
}

export interface UserRoleAssignment {
  id: string;
  oid: string;
  upn: string;
  displayName: string;
  role: UserRole;
  assignedByName?: string;
  assignedOn?: string;
}

export const SECTION_LABELS: Record<SectionKey, string> = {
  exec_summary: 'Executive Summary',
  safety: 'Safety / People',
  customer: 'Customer',
  sales: 'Sales',
  operations: 'Operations',
  financial: 'Financial',
};

export const SECTION_KEYS: SectionKey[] = [
  'exec_summary', 'safety', 'customer', 'sales', 'operations', 'financial',
];

export const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

const MONTH_ABBR = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

export function formatMonthYear(month: number, year: number): string {
  return `${MONTH_ABBR[month - 1]}-${year}`;
}
